import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authClient } from "@/lib/auth";
import { useUpdateUserName } from "@/mutations/user-mutations";
import { AvatarUpload } from "./AvatarUpload";

function SubTitle({ children }: { children: React.ReactNode }) {
    return <h4 className="text-lg mb-4 not-first:mt-6 font-medium">{children}</h4>;
}

type UpdateNameFormData = {
    name: string;
};

export default function ProfileSettings() {
    const session = authClient.useSession();
    const currentUser = session.data?.user;
    const updateNameMutation = useUpdateUserName();

    const {
        register,
        handleSubmit,
        resetField,
        formState: { errors, isDirty },
        setError,
    } = useForm<UpdateNameFormData>({
        defaultValues: {
            name: currentUser?.name || "",
        },
        mode: "onBlur",
    });

    const onSubmit = async (data: UpdateNameFormData) => {
        // Basic validation
        if (!data.name || data.name.trim().length === 0) {
            setError("name", { message: "Name is required" });
            return;
        }
        if (data.name.trim().length < 2) {
            setError("name", { message: "Name must be at least 2 characters long" });
            return;
        }

        await updateNameMutation.mutateAsync({ name: data.name.trim() });
        resetField("name", { defaultValue: data.name.trim() });

        toast.success("Name updated", {
            description: "Your name has been updated successfully",
        });
    };

    if (!currentUser) {
        return null;
    }

    return (
        <>
            <SubTitle>Name</SubTitle>
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-row gap-2">
                <div className="space-y-2">
                    <Input data-1p-ignore id="name" className="w-56" placeholder={session.data?.user?.name} {...register("name")} />
                    {errors.name && <p className=" text-red-500">{errors.name.message}</p>}
                </div>
                <Button type="submit" variant="outline" disabled={updateNameMutation.isPending || !isDirty}>
                    {updateNameMutation.isPending ? "Updating..." : "Update Name"}
                </Button>
            </form>
            <SubTitle>Profile Picture</SubTitle>
            <AvatarUpload />
        </>
    );
}
